import {
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';
import { Alert } from '@interfaces/alert/alert';
import { TeamMember } from '@interfaces/code-team/team-member';
import { AlertService } from '@services/alert.service';
import { AssignmentService } from '@services/assignment.service';
import { AuthService } from '@services/auth.service';
import { CodeTeamService } from '@services/code-team.service';
import { DataService } from '@services/data.service';
import { LocaleService } from '@services/local.service';
import { ModalService } from '@services/modal.service';
import { PatientService } from '@services/patient.service';
import { PushNotificationService } from '@services/push-notification.service';
import { RoleService } from '@services/role.service';
import { RoomLocationService } from '@services/room-location.service';
import { SecurityAssignmentService } from '@services/security-assignment.service';
import { ShiftService } from '@services/shift.service';
import { StaffService } from '@services/staff.service';
import { TaskService } from '@services/task.service';
import { ToastService } from '@services/toast.service';
import { WebSocketService } from '@services/web-socket.service';
import { ToastsComponent } from '@shared/components/toasts/toasts.component';
import { catchError } from 'rxjs';
import { TaskStatus } from './enums/task-status';
import { TopicType } from './enums/topic-type';
import { UserRole } from './enums/user-role';
import { TranslateService } from '@ngx-translate/core';
import { DepartmentService } from './services/department.service';
import dayjs from 'dayjs';
import { MobileHeartBeatService } from '@services/mobile-heartbeat.service';
import { LocationOfUser } from '@interfaces/modile-heart-beat/location-of-user';
import { environment } from 'src/environments/environment';
import { FinPingService } from '@services/fin-ping.service';
import { ReportsService } from '@services/reports.service';
import { AlarmNotification } from '@interfaces/alarm/alarm-notification';
import { mapAlarmToTask } from './pipes/mapAlarmToTask';
import { mapAlarmRowsToTasks } from './pipes/mapAlarmRowsToTasks';
import { SettingsService } from '@services/settings.service';
import { AuthStore } from '@stores/auth.store';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, ToastsComponent],
})
export class AppComponent implements OnDestroy {
  private localService = inject(LocaleService);
  private router = inject(Router);
  private authStore = inject(AuthStore);
  private settingsService = inject(SettingsService);
  private webSocketService = inject(WebSocketService);
  private authService = inject(AuthService);
  private taskService = inject(TaskService);
  private alertService = inject(AlertService);
  private dataService = inject(DataService);
  private codeTeamService = inject(CodeTeamService);
  private roleService = inject(RoleService);
  private roomLocationService = inject(RoomLocationService);
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);
  private assignmentService = inject(AssignmentService);
  private shiftService = inject(ShiftService);
  private patientService = inject(PatientService);
  private staffService = inject(StaffService);
  private mobileHeartBeatService = inject(MobileHeartBeatService);
  private securityAssignmentService = inject(SecurityAssignmentService);
  private departmentService = inject(DepartmentService);
  private reportsService = inject(ReportsService);
  private currentUser = computed(() => this.authService.user());
  private icons: {
    label: string;
    path: string;
  }[] = [
    {
      label: 'pin',
      path: this.setPathName('pin'),
    },
  ];

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private pushNotificationService: PushNotificationService,
    private translateService: TranslateService,
    private finPingService: FinPingService,
  ) {
    this.icons.forEach((icon) => {
      this.matIconRegistry.addSvgIcon(
        icon.label,
        this.domSanitizer.bypassSecurityTrustResourceUrl(icon.path),
      );
    });

    let uuid = this.webSocketService.uuid;
    this.webSocketService.initConnection().subscribe({
      next: ({ topic, ...res }) => {
        switch (topic) {
          case TopicType.USER_LOGIN_RESPONSE: {
            if (uuid === res.key) {
              this.authService.setUser(res);
            }
            break;
          }
          case TopicType.SECURITY_ASSIGNMENT_DATA_RESPONSE: {
            if (res?.data?.securityAssignment) {
              this.securityAssignmentService.setSecurityAssignments(
                res.data.securityAssignment,
              );
            }
            break;
          }
          case TopicType.UPDATE_REPORT_RESPONSE: {
            if (res?.message?.data) {
              this.reportsService.updateReport(res.message.data);

              this.toastService.showSuccessMessage(
                'front.toast.filter-updated',
              );
            }
            break;
          }
          case TopicType.CONFIG_DATA_RESPONSE: {
            if (this.currentUser()?.id && uuid === res.key) {
              this.dataService.setData(res);
            }
            break;
          }
          case TopicType.TASK_DATA_RESPONSE: {
            if (res?.data?.task && uuid === res.key) {
              // IMPORTANT!! Changed to addTasks cause NURSE_CALL response should be concat with this topic
              this.taskService.addTasks(res.data.task, res.data.pageInfo);
            }
            if (uuid === res.key) {
              this.taskService.finishDrawerLoading('task');
            }
            break;
          }
          case TopicType.GENERATE_REPORT_RESPONSE: {
            if (!res.status) {
              this.toastService.showInfoMessageWithErorrMessage(
                'front.reporting.error',
                res.message?.error || 'Unknown error',
              );
            }

            let messageKeys = ['codes', 'alarms', 'nurseCalls'];
            const data = res.message.data || {};

            const allEmpty = messageKeys.every(
              (key) => Array.isArray(data[key]) && data[key].length === 0,
            );

            if (allEmpty && res.status) {
              this.toastService.showSuccessMessage('front.reporting.no-data');
            } else {
              this.toastService.showSuccessMessage(
                'front.reporting.resived-data',
              );
            }

            this.reportsService.setReports(data);
            break;
          }

          case TopicType.ALERT_DATA_RESPONSE: {
            if (res?.data?.alert && uuid === res.key) {
              this.alertService.setAlerts(res.data.alert);
            }
            break;
          }
          case TopicType.CREATE_REPORT_RESPONSE: {
            if (res?.message?.data) {
              this.reportsService.addReadReports(res.message.data);
              this.toastService.showSuccessMessage(
                'front.toast.filter-created',
              );
            }
            break;
          }
          case TopicType.STAFF_DATA_RESPONSE: {
            const teamMembers: TeamMember[] =
              res?.data?.staff?.length &&
              res?.data?.staff[0].departmentStaff.length &&
              res?.data?.staff[0].departmentStaff[0] &&
              res?.data?.staff[0].departmentStaff[0].roleStaff.length
                ? res.data.staff
                    .flatMap((s: any) => s.departmentStaff)
                    .flatMap((s: any) => s.roleStaff)
                : [];
            this.staffService.setTeamMembers(teamMembers);
            break;
          }
          case TopicType.PATIENT_DATA_RESPONSE: {
            if (res?.data?.patient) {
              this.patientService.setPatients(res.data.patient);
            }
            break;
          }
          case TopicType.ASSISTREQ_NOTIFICATION: {
            if (
              res?.data &&
              res?.targets?.includes(this.currentUser()?.id?.replace('@', ''))
            ) {
              this.taskService.addAssistRequestTask(res);
              if (!environment.isWlmhEnabled) {
                this.toastService.showSuccessMessage(
                  'front.toast.task-created',
                );
              }
              this.sendPushNotification({
                title: this.translateService.instant(
                  'front.push-notification.new-task-title',
                ),
                body: this.translateService.instant(
                  'front.push-notification.new-task-body',
                ),
                audioPath: 'assets/voice/task.mp3',
              });
            }
            break;
          }
          case TopicType.ASSISTREQ_STATUS_UPDATE: {
            if (res?.data) {
              this.taskService.updateAssistRequestTask(res);
            }
            break;
          }
          case TopicType.ASSISTREQ_DATA_RESPONSE: {
            if (res?.data?.assistreq && uuid === res.key) {
              this.taskService.addAssistRequestTasks(
                res.data.assistreq,
                res.data.pageInfo,
              );
            }
            if (uuid === res.key) {
              this.taskService.finishDrawerLoading('assistreq');
            }
            break;
          }
          case TopicType.SHIFT_DATA_RESPONSE: {
            /*to avoid the change on the manage assignments page when two Unit administrator are logged in*/
            if (res.key !== uuid) {
              return;
            }
            if (
              res.data.shift &&
              res.data.shift[0] &&
              res.data.shift[0].shifts
            ) {
              this.shiftService.shiftRequests.set(res.data.shift[0].shifts);
            }
            break;
          }
          case TopicType.ASSIGNMENT_DATA_RESPONSE: {
            /*to avoid the change on the manage assignments page when two Unit administrator are logged in*/
            if (res.key !== uuid) {
              return;
            }
            this.assignmentService.setPageInfo(res.data.pageInfo);
            this.assignmentService.setData(res.data.assignment);
            break;
          }
          case TopicType.GLOBALCONFIG_UPDATE_RESPONSE: {
            if (this.currentUser()?.id && res.status) {
              this.authService.updateUserGlobalConfig(res.message.data);
            }
            break;
          }
          case TopicType.LOCATION_DATA_RESPONSE: {
            if (this.currentUser()?.id && uuid === res.key && res.status) {
              this.roomLocationService.setLocations(res.data);
            }
            break;
          }
          case TopicType.ROLE_DATA_RESPONSE: {
            if (this.currentUser()?.id && res.status) {
              this.roleService.setRoles(res.data);
            }
            break;
          }
          case TopicType.GET_USERS_RESPONSE: {
            if (this.currentUser()?.id && uuid === res.key) {
              this.codeTeamService.setTeamMembers(res);
            }
            break;
          }
          case TopicType.DEPARTMENT_DATA_RESPONSE: {
            if (this.currentUser()?.id && uuid === res.key) {
              this.departmentService.setDepartments(res?.data?.department);
            }
            break;
          }
          case TopicType.ALARM_NOTIFICATION: {
            const me = this.currentUser()?.id?.replace(/^@/, '');

            if (this.currentUser()?.role === UserRole.UNIT_ADMINISTRATOR) {
              const alarm = res as AlarmNotification;

              if (me && alarm.targets.includes(me)) {
                const task = mapAlarmToTask(alarm);

                this.taskService.addTask(task, TaskStatus.ACTIVE, false);

                if (!environment.isWlmhEnabled) {
                  this.toastService.showSuccessMessage(
                    'front.toast.task-created',
                  );
                }

                const departmentName = alarm?.data?.location?.department;
                const alarmAudioPath = this.getAlarmAudioPath(
                  alarm?.data?.description,
                );

                this.sendPushNotificationForNurseCall({
                  title: this.translateService.instant(
                    'front.push-notification.new-alarm-title',
                  ),
                  body: this.translateService.instant(
                    'front.push-notification.new-alarm-body',
                  ),
                  departmentName,
                  audioPath: alarmAudioPath,
                });
              }
            }
            break;
          }

          case TopicType.ALARM_STATUS_UPDATE: {
            if (this.currentUser()?.id) {
              if (UserRole.UNIT_ADMINISTRATOR === this.currentUser()?.role) {
                this.taskService.updateTask(res);
              }
            }
            break;
          }

          case TopicType.ALARM_DATA_RESPONSE: {
            if (this.currentUser()?.id) {
              if (UserRole.UNIT_ADMINISTRATOR === this.currentUser()?.role) {
                const rows = res?.data?.alarm ?? [];

                const tasks = mapAlarmRowsToTasks(rows);

                this.taskService.addTasks(tasks, res.data.pageInfo, false);
                this.taskService.finishDrawerLoading('alarm');
              }
            }
            break;
          }

          case TopicType.CODE_DATA_RESPONSE: {
            if (this.currentUser()?.id) {
              if (UserRole.LOCATING_OPERATOR === this.currentUser()?.role) {
                this.codeTeamService.updateCodes(res.data);
              }
            }
            break;
          }
          case TopicType.TASK_STATUS_UPDATE: {
            if (this.currentUser()?.id) {
              if (UserRole.UNIT_ADMINISTRATOR === this.currentUser()?.role) {
                this.taskService.updateTask(res);
              }
            }
            break;
          }
          //nurse-call
          case TopicType.NURSE_CALL_NOTIFICATION: {
            if (UserRole.UNIT_ADMINISTRATOR === this.currentUser()?.role) {
              if (
                (this.currentUser()?.id &&
                  this.currentUser()?.id.includes(res.target)) ||
                !res.target
              ) {
                this.taskService.addTask(res, TaskStatus.ACTIVE, false);
                if (!environment.isWlmhEnabled) {
                  this.toastService.showSuccessMessage(
                    'front.toast.task-created',
                  );
                }

                const departmentName = res?.data?.location?.department;

                this.sendPushNotificationForNurseCall({
                  title: this.translateService.instant(
                    'front.push-notification.new-task-title',
                  ),
                  body: this.translateService.instant(
                    'front.push-notification.new-task-body',
                  ),
                  departmentName,
                });
              }
            }
            break;
          }

          case TopicType.NURSE_CALL_STATUS_UPDATE: {
            if (this.currentUser()?.id) {
              if (UserRole.UNIT_ADMINISTRATOR === this.currentUser()?.role) {
                this.taskService.updateTask(res);
              }
            }
            break;
          }

          case TopicType.NURSE_CALL_DATA_RESPONSE: {
            if (this.currentUser()?.id) {
              if (UserRole.UNIT_ADMINISTRATOR === this.currentUser()?.role) {
                this.taskService.addTasks(
                  res.data.nurseCall,
                  res.data.pageInfo,
                  false,
                );
                this.taskService.finishDrawerLoading('nurse');
              }
            }
            break;
          }
          case TopicType.TASK_NOTIFICATION: {
            if (UserRole.UNIT_ADMINISTRATOR === this.currentUser()?.role) {
              if (
                (this.currentUser()?.id &&
                  this.currentUser()?.id.includes(res.target)) ||
                !res.target
              ) {
                this.taskService.addTask(res, TaskStatus.ACTIVE);

                //TODO TEMP option
                if (!environment.isWlmhEnabled) {
                  // const audio = new Audio('assets/voice/notification.mp3');
                  // audio.play();

                  this.toastService.showSuccessMessage(
                    'front.toast.task-created',
                  );
                }

                this.sendPushNotification({
                  title: this.translateService.instant(
                    'front.push-notification.new-task-title',
                  ),
                  body: this.translateService.instant(
                    'front.push-notification.new-task-body',
                  ),
                  audioPath: 'assets/voice/task.mp3',
                });
              }
            }
            break;
          }

          case TopicType.USER_UPDATE_RESPONSE: {
            const storedDeviceId = sessionStorage.getItem('deviceId');
            const isSameDevice =
              !storedDeviceId ||
              storedDeviceId === res?.message?.data?.deviceId;

            if (
              res.message.data.availabilityStatus === 'offline' &&
              res.message.data.userId === this.currentUser()?.id &&
              isSameDevice
            ) {
              this.settingsService.reset();
              this.authStore.logOut();
              this.router.navigateByUrl('/login');

              this.toastService.showInfoMessage(
                'front.toast.logged-out',
                '',
                1000,
              );
            }
            if (
              res.status &&
              uuid === res.key &&
              this.currentUser()?.id == res.message.data.userId &&
              isSameDevice
            ) {
              this.authService.updateAvailabilityStatus(
                res.message.data.availabilityStatus,
              );
            }
            break;
          }
          case TopicType.PING: {
            this.authService.lastPingTime.set(dayjs().toString());
            break;
          }
          //codes
          case TopicType.ALERT_NOTIFICATION: {
            if (
              this.currentUser()?.id &&
              res.targets?.length &&
              res.targets.includes(this.currentUser()?.id?.replace('@', ''))
            ) {
              if (
                UserRole.LOCATING_OPERATOR === this.currentUser()?.role ||
                UserRole.UNIT_ADMINISTRATOR === this.currentUser()?.role
              ) {
                this.alertService.addAlert(res);
                if (!environment.isWlmhEnabled) {
                  this.modalService.showSuccessModal();
                }

                this.sendPushNotification({
                  title: this.translateService.instant(
                    'front.push-notification.new-alert-title',
                  ),
                  body: this.translateService.instant(
                    'front.push-notification.new-alert-body',
                  ),
                });
              }
            }
            break;
          }
          case TopicType.ALERT_STATUS_UPDATE: {
            if (this.currentUser()?.id) {
              if (
                UserRole.LOCATING_OPERATOR === this.currentUser()?.role ||
                UserRole.UNIT_ADMINISTRATOR === this.currentUser()?.role
              ) {
                const updatedAlert: Alert = res;
                if (
                  UserRole.LOCATING_OPERATOR === this.authService.user().role
                ) {
                  this.alertService.updateAlert(updatedAlert);
                }
                if (
                  UserRole.UNIT_ADMINISTRATOR === this.authService.user().role
                ) {
                  if ('closed' === updatedAlert.status) {
                    this.alertService.removeAlertFromList(updatedAlert);
                  } else {
                    this.alertService.updateAlert(updatedAlert);
                  }
                }
              }
            }
            break;
          }
          case TopicType.DELETE_REPORT_RESPONSE: {
            if (res?.message?.data) {
              this.reportsService.deleteFilterFromStore(res.message.data.id);
              this.toastService.showSuccessMessage(
                'front.toast.filter-deleted',
              );
            }
            break;
          }
          case TopicType.MOBILE_HEART_BEAT: {
            if (this.currentUser()?.id) {
              if (UserRole.PORTAL_ADMINISTRATOR === this.currentUser()?.role) {
                const location: LocationOfUser = res;

                this.mobileHeartBeatService.updateLocation(location);
              }
            }
            break;
          }
          case TopicType.READ_REPORT_RESPONSE: {
            if (this.currentUser()?.id) {
              this.reportsService.setReadReports(res);
            }
            break;
          }
        }
      },
      error: (err) => {
        return catchError(err);
      },
      complete: console.log,
    });

    // Request room locations for all roles once a user is present
    effect(() => {
      const uid = this.currentUser()?.id;
      if (uid) {
        this.roomLocationService.sendLocationDataRequest();
      }
    });
    this.localService.initializeLocale();
    this.startKeepAlivePing();
  }

  private keepAliveIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly keepAliveIntervalMs = 50 * 60 * 1000;

  private startKeepAlivePing() {
    if (this.keepAliveIntervalId) return;

    this.keepAliveIntervalId = setInterval(() => {
      this.webSocketService.send({
        topic: TopicType.WEB_PING,
        message: {},
      });
    }, this.keepAliveIntervalMs);
  }

  ngOnDestroy(): void {
    if (this.keepAliveIntervalId) {
      clearInterval(this.keepAliveIntervalId);
      this.keepAliveIntervalId = null;
    }
  }

  private isInPopupNotifications(departmentName?: string): boolean {
    if (!departmentName) return false;

    try {
      const raw = sessionStorage.getItem('user');
      if (!raw) return false;

      const data = JSON.parse(raw);

      let arr: unknown = data?.user?.notificationSettings?.popupNotifications;

      if (typeof arr === 'string') {
        try {
          arr = JSON.parse(arr);
        } catch {
          arr = undefined;
        }
      }

      if (!Array.isArray(arr)) return false;

      return (arr as string[])
        .map((d) => d.trim().toLowerCase())
        .includes(departmentName.trim().toLowerCase());
    } catch {
      return false;
    }
  }

  private sendPushNotificationForNurseCall({
    title,
    body,
    departmentName,
    audioPath = 'assets/voice/task.mp3',
  }: {
    title?: string;
    body?: string;
    departmentName?: string;
    audioPath?: string;
  }) {
    if (this.isInPopupNotifications(departmentName)) {
      const audio = new Audio(audioPath);
      audio.play();
    }

    this.pushNotificationService.showNotification(
      title ||
        this.translateService.instant('front.push-notification.new-task-title'),
      body ||
        this.translateService.instant('front.push-notification.new-task-body'),
    );
  }

  private getAlarmAudioPath(description?: string): string {
    if (!description) return 'assets/voice/alarm.mp3';

    const normalized = description.trim().toLowerCase();
    const audioByDescription: Record<string, string> = {
      'cleaning call': 'assets/voice/bing-bong-dong.mp3',
      'cord out': 'assets/voice/bing_660-2_10.mp3',
      'medicine call': 'assets/voice/bing-bong-dong.mp3',
      'pain call': 'assets/voice/bing-bong-dong.mp3',
      'patient call': 'assets/voice/bing-bong-dong.mp3',
      'staff assist': 'assets/voice/bing_660-2_10.mp3',
      'washroom call': 'assets/voice/bing-bong-dong.mp3',
      'water call': 'assets/voice/bing-bong-dong.mp3',
      'bed alarm': 'assets/voice/bing_660-2_10.mp3',
    };

    return audioByDescription[normalized] || 'assets/voice/alarm.mp3';
  }

  sendPushNotification({
    title,
    body,
    audioPath = 'assets/voice/buzzer_1336-2hz.mp3',
  }: {
    title?: string;
    body?: string;
    audioPath?: string;
  }) {
    const audio = new Audio(audioPath);
    audio.play();

    this.pushNotificationService.showNotification(
      title ||
        this.translateService.instant(
          'front.push-notification.new-alert-title',
        ),
      body ||
        this.translateService.instant('front.push-notification.new-alert-body'),
    );
  }

  setPathName(name: string) {
    return `/assets/images/svg/${name}.svg`;
  }
}
